import React, { useState, useEffect } from "react";
import { apiFetch } from "../utils/api";
import { toast } from "../components/Toast";
import { io } from "socket.io-client";
import LiveMap from "../components/LiveMap";
import MapErrorBoundary from "../components/MapErrorBoundary";
import API_BASE_URL from "../config/api";

const ReturnTripsMap = () => {
  const [stats, setStats] = useState({
    totalVehicles: 0,
    returningTrips: 0,
    activeVehicles: 0,
    parkedVehicles: 0,
  });
  const [loading, setLoading] = useState(true);

  // Live GPS data keyed by vehicle_id
  const [liveVehicles, setLiveVehicles] = useState({});

  // Returning trips are the primary identity
  const [returningTrips, setReturningTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [scrollIndex, setScrollIndex] = useState(0);
  const [busSearch, setBusSearch] = useState("");

  const selectedTrip =
    returningTrips.find((t) => t.trip_id === selectedTripId) || null;

  // Filter trips by bus code search
  const filteredTrips = busSearch
    ? returningTrips.filter((t) =>
        (t.vehicle_code || t.bus_number || "")
          .toLowerCase()
          .includes(busSearch.toLowerCase()),
      )
    : returningTrips;

  const VISIBLE_COUNT = 8;
  const canScrollLeft = scrollIndex > 0;
  const canScrollRight = scrollIndex + VISIBLE_COUNT < filteredTrips.length;

  // Fetch dashboard data
  useEffect(() => {
    const loadDash = async () => {
      setLoading(true);
      try {
        const [vRes, tRes] = await Promise.all([
          apiFetch("/vehicles").catch(() => ({ data: [] })),
          apiFetch("/trips").catch(() => ({ data: [] })),
        ]);

        const tripsList = tRes.data || [];
        const returning = tripsList.filter((t) => t.status === "returning");

        setReturningTrips(returning);

        // Auto-select first returning trip
        if (returning.length > 0) {
          setSelectedTripId((prev) => prev || returning[0].trip_id);
        }

        // Clear stale live data for vehicles no longer returning
        setLiveVehicles((prev) => {
          const returningVehicleIds = new Set(
            returning.map((t) => String(t.vehicle_id)),
          );
          const next = {};
          for (const vid of Object.keys(prev)) {
            if (returningVehicleIds.has(vid)) next[vid] = prev[vid];
          }
          return next;
        });

        // Total = all enabled (non-deleted, non-disabled) vehicles from DB
        const totalVehicles = (vRes.data || []).filter(
          (v) => v.is_active === 1 || v.is_active === true,
        ).length;
        // Trip-based counts
        const activeVehicles = tripsList.filter(
          (t) => t.status === "active",
        ).length;
        const returningCount = returning.length;
        // Parked = everything else (scheduled trips + unassigned vehicles)
        const parkedVehicles = totalVehicles - activeVehicles - returningCount;

        setStats({
          totalVehicles,
          returningTrips: returningCount,
          activeVehicles,
          parkedVehicles: Math.max(0, parkedVehicles),
        });
      } catch (e) {
        toast.error("Failed to load returning trips.");
      } finally {
        setLoading(false);
      }
    };
    loadDash();
  }, []);

  // Poll trips every 10s to keep returning list fresh
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const tRes = await apiFetch("/trips");
        const tripsList = tRes.data || [];
        const returning = tripsList.filter((t) => t.status === "returning");
        setReturningTrips(returning);

        // Recalculate vehicle status counts
        const activeVehicles = tripsList.filter(
          (t) => t.status === "active",
        ).length;
        const returningTrips = returning.length;
        const parkedVehicles =
          stats.totalVehicles - (activeVehicles + returningTrips);

        setStats((prev) => ({
          ...prev,
          returningTrips,
          activeVehicles,
          parkedVehicles: Math.max(0, parkedVehicles),
        }));
        setLiveVehicles((prev) => {
          const returningVehicleIds = new Set(
            returning.map((t) => String(t.vehicle_id)),
          );
          const next = {};
          for (const vid of Object.keys(prev)) {
            if (returningVehicleIds.has(vid)) next[vid] = prev[vid];
          }
          return next;
        });
      } catch (e) {
        /* silent */
      }
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // WebSocket — GPS updates keyed by vehicle_id
  useEffect(() => {
    console.log("[ReturnTripsMap] Connecting to:", API_BASE_URL);
    const socket = io(API_BASE_URL, {
      transports: ["websocket", "polling"],
      timeout: 10000,
      withCredentials: true,
    });

    socket.on("connect", () => {
      console.log("[ReturnTripsMap] Socket connected, ID:", socket.id);
    });

    socket.on("connect_error", (err) => {
      console.error("[ReturnTripsMap] Socket connection error:", err.message);
    });

    socket.on("global_gps_update", (payload) => {
      console.log(
        "[ReturnTripsMap] GPS received:",
        payload.vehicle_id,
        payload.latitude,
        payload.longitude,
      );
      setLiveVehicles((prev) => ({
        ...prev,
        [String(payload.vehicle_id)]: payload,
      }));
    });

    socket.on("bus_status", (payload) => {
      console.log("[ReturnTripsMap] Bus status:", payload);
      if (
        payload.status === "stopped" ||
        payload.status === "completed" ||
        payload.status === "scheduled"
      ) {
        setLiveVehicles((prev) => {
          const next = { ...prev };
          delete next[String(payload.vehicle_id)];
          return next;
        });
      }
    });

    return () => socket.disconnect();
  }, []);

  // Poll REST API for selected return trip live GPS as a fallback
  useEffect(() => {
    if (!selectedTrip) return;

    const interval = setInterval(async () => {
      try {
        const gpsRes = await apiFetch(`/gps/live/${selectedTrip.vehicle_id}`);
        const gpsData = gpsRes?.data ?? gpsRes;
        if (
          gpsData &&
          gpsData.latitude !== undefined &&
          gpsData.longitude !== undefined
        ) {
          setLiveVehicles((prev) => ({
            ...prev,
            [String(selectedTrip.vehicle_id)]: gpsData,
          }));
        }
      } catch (err) {
        // silent fallback if REST live data is unavailable yet
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [selectedTrip]);

  const MetricCard = ({ title, val, icon }) => (
    <div
      className="glass-panel"
      style={{
        padding: "24px",
        display: "flex",
        alignItems: "center",
        gap: "20px",
      }}
    >
      <div style={{ fontSize: "2.5rem" }}>{icon}</div>
      <div>
        <div
          style={{
            color: "var(--text-muted)",
            fontSize: "0.9rem",
            textTransform: "uppercase",
            letterSpacing: "1px",
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: "2.5rem", fontWeight: "800", color: "#000" }}>
          {loading ? "-" : val}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <h1 className="page-title text-gradient" style={{ marginBottom: "32px" }}>
        Return Trips Overview
      </h1>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "24px",
          marginBottom: "32px",
        }}
      >
        <MetricCard
          title="Total Vehicles"
          val={stats.totalVehicles}
          icon="🚌"
        />
        <MetricCard
          title="Returning Trips"
          val={stats.returningTrips}
          icon="🔄"
        />
        <MetricCard
          title="Active Vehicles"
          val={stats.activeVehicles}
          icon="⚡"
        />
        <MetricCard
          title="Vehicles Parked"
          val={stats.parkedVehicles}
          icon="🅿️"
        />
      </div>

      {/* LIVE RETURNING FLEET TRACKING */}
      <div
        className="glass-panel"
        style={{
          padding: "24px",
          marginBottom: "32px",
          border: "1px solid var(--border-color)",
        }}
      >
        <h3
          style={{
            borderBottom: "1px solid var(--border-color)",
            paddingBottom: "16px",
            marginBottom: "24px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>🔶 Live Returning Fleet</span>
          <span style={{ fontSize: "0.9rem", color: "var(--text-muted)" }}>
            {returningTrips.length} Returning Bus
            {returningTrips.length !== 1 ? "es" : ""}
          </span>
        </h3>

        {returningTrips.length === 0 ? (
          <p
            style={{
              color: "var(--text-muted)",
              textAlign: "center",
              padding: "32px 16px",
            }}
          >
            No returning trips running right now.
          </p>
        ) : (
          <div>
            {/* BUS CODE SEARCH */}
            <div style={{ marginBottom: "16px" }}>
              <input
                type="text"
                placeholder="Search by bus code..."
                value={busSearch}
                onChange={(e) => {
                  setBusSearch(e.target.value);
                  setScrollIndex(0);
                }}
                style={{
                  borderRadius: "6px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-secondary)",
                  color: "var(--text-primary)",
                  width: "200px",
                  height: "40px",
                }}
              />
            </div>

            {/* BUS SELECTOR with scroll arrows */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "24px",
              }}
            >
              {canScrollLeft && (
                <button
                  onClick={() =>
                    setScrollIndex((prev) => Math.max(0, prev - 1))
                  }
                  style={{
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-secondary)",
                    cursor: "pointer",
                    fontSize: "1.2rem",
                  }}
                >
                  ←
                </button>
              )}

              <div style={{ display: "flex", gap: "10px", overflow: "hidden" }}>
                {filteredTrips
                  .slice(scrollIndex, scrollIndex + VISIBLE_COUNT)
                  .map((trip) => (
                    <div
                      key={trip.trip_id}
                      className={`bus-icon active-trip${selectedTripId === trip.trip_id ? " selected" : ""}`}
                      onClick={() => setSelectedTripId(trip.trip_id)}
                      style={{ minWidth: "80px" }}
                    >
                      <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>
                        {trip.vehicle_code || trip.bus_number || "—"}
                      </div>
                      <div
                        style={{
                          fontSize: "0.7rem",
                          opacity: 0.75,
                          marginTop: "2px",
                        }}
                      >
                        {trip.registration_no || "—"}
                      </div>
                    </div>
                  ))}
              </div>

              {canScrollRight && (
                <button
                  onClick={() => setScrollIndex((prev) => prev + 1)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-secondary)",
                    cursor: "pointer",
                    fontSize: "1.2rem",
                  }}
                >
                  →
                </button>
              )}
            </div>

            {/* SELECTED BUS DETAIL + MAP */}
            {selectedTrip
              ? (() => {
                  const vid = String(selectedTrip.vehicle_id);
                  const liveData = liveVehicles[vid];
                  console.log(
                    "[ReturnTripsMap] Render - selected vehicle:",
                    vid,
                    "liveData found:",
                    !!liveData,
                    "available vehicles:",
                    Object.keys(liveVehicles),
                  );
                  return (
                    <div>
                      {/* Header bar */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "12px 0",
                          marginBottom: "12px",
                          borderBottom: "1px solid var(--border-color)",
                        }}
                      >
                        <div>
                          <span
                            style={{ fontWeight: 700, fontSize: "1.05rem" }}
                          >
                            {selectedTrip.vehicle_code ||
                              selectedTrip.bus_number ||
                              "—"}
                          </span>
                          <span
                            style={{
                              color: "var(--text-muted)",
                              marginLeft: "10px",
                              fontSize: "0.9rem",
                            }}
                          >
                            Vehicle: {selectedTrip.registration_no || "—"}
                          </span>
                          <span
                            style={{
                              color: "var(--text-muted)",
                              marginLeft: "10px",
                              fontSize: "0.9rem",
                            }}
                          >
                            Driver: {selectedTrip.driver_name || "—"}
                          </span>
                          <span
                            style={{
                              color: "var(--text-muted)",
                              marginLeft: "10px",
                              fontSize: "0.9rem",
                            }}
                          >
                            Phone: {selectedTrip.driver_mobile || "—"}
                          </span>
                          <span
                            style={{
                              color: "var(--text-muted)",
                              marginLeft: "10px",
                              fontSize: "0.9rem",
                            }}
                          >
                            Route: {selectedTrip.route_name || "—"}
                          </span>
                        </div>
                        <span
                          style={{
                            color: "#f59e0b",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                          }}
                        >
                          🟠 Returning
                        </span>
                      </div>

                      {/* Map */}
                      <MapErrorBoundary vehicleId={selectedTrip.vehicle_id}>
                        <LiveMap
                          key={selectedTrip.vehicle_id}
                          vehicleId={String(selectedTrip.vehicle_id)}
                          liveData={liveData}
                        />
                      </MapErrorBoundary>

                      {/* Live stats */}
                      {liveData && (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(160px, 1fr))",
                            gap: "12px",
                            marginTop: "16px",
                          }}
                        >
                          <div
                            style={{
                              background: "var(--bg-alt)",
                              padding: "12px",
                              borderRadius: "8px",
                              border: "1px solid var(--border-color)",
                            }}
                          >
                            <div
                              style={{
                                color: "var(--text-muted)",
                                fontSize: "0.8rem",
                              }}
                            >
                              📡 Coordinates
                            </div>
                            <div
                              style={{
                                fontFamily: "monospace",
                                fontSize: "0.85rem",
                                marginTop: "4px",
                              }}
                            >
                              {Number(liveData.latitude).toFixed(4)},{" "}
                              {Number(liveData.longitude).toFixed(4)}
                            </div>
                          </div>
                          <div
                            style={{
                              background: "var(--bg-alt)",
                              padding: "12px",
                              borderRadius: "8px",
                              border: "1px solid var(--border-color)",
                            }}
                          >
                            <div
                              style={{
                                color: "var(--text-muted)",
                                fontSize: "0.8rem",
                              }}
                            >
                              ⏩ Speed
                            </div>
                            <div
                              style={{ fontSize: "0.85rem", marginTop: "4px" }}
                            >
                              {liveData.speed || 0} km/h
                            </div>
                          </div>
                          <div
                            style={{
                              background: "var(--bg-alt)",
                              padding: "12px",
                              borderRadius: "8px",
                              border: "1px solid var(--border-color)",
                            }}
                          >
                            <div
                              style={{
                                color: "var(--text-muted)",
                                fontSize: "0.8rem",
                              }}
                            >
                              🕐 Last Update
                            </div>
                            <div
                              style={{ fontSize: "0.85rem", marginTop: "4px" }}
                            >
                              {new Date(
                                liveData.timestamp,
                              ).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                      )}
                      {!liveData && (
                        <p
                          style={{
                            color: "var(--text-muted)",
                            marginTop: "12px",
                            fontSize: "0.9rem",
                          }}
                        >
                          Waiting for GPS data...
                        </p>
                      )}
                    </div>
                  );
                })()
              : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReturnTripsMap;
